import {css, useTheme} from '@mui/material';
import {
  asQueryString,
  ConfigObjectType,
  DlMuiButton,
  DlMuiDynamicIcon,
  GetStylesFunction,
  IconHeaderWithSearch,
  LastWeekMonthIso,
  NoDataMessageWithIcon,
  useLgOrLarger,
  useSize,
  useXlOrLarger,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  ChartColor,
  Formatters,
  getBarChartHeight,
  getBarChartItems,
  getDefaultMTOptions,
  HaidiEncounterLinkFormatter,
  HaidiHaiTypeFormatter,
  JustTooltipFormatter,
  LightBarChartTrace,
  LightChart,
  LightChartLayout,
  MaterialTableWrapper,
  NoHais,
} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import {Column} from 'material-table';
import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';

import {DashboardDataType} from './HaidiDashboardLayoutContainer';
import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {HaiListLinkFilters} from '../hai';

type RowType = DashboardDataType['lastReportedHais'][0];
function useColumns(formatters: Formatters, config: ConfigObjectType): Column<RowType>[] {
  const {t, formatDate} = formatters;
  const lgOrLarger = useLgOrLarger();
  const xlOrLarger = useXlOrLarger();
  const {getBySize} = useSize({
    bools: [lgOrLarger, xlOrLarger],
    sizes: ['sm', 'lg', 'xl'] as const,
  });
  const {getTranslationForStandardTypeId, getCodeLabelForStandardTypeId} = useStandardTypes({config});
  const columns: Column<RowType>[] = [
    {
      field: 'name',
      title: t('dashboard.table.name'),
      render: (data) => <HaidiEncounterLinkFormatter {...AG_GRID_COMPATIBILITY} data={data} value={data?.patientName} />,
    },
    {
      field: 'patientIdentificationNumber',
      title: t('dashboard.table.patientIdentificationNumber'),
      tooltip: t('dashboard.table.patientIdentificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.patientIdentificationNumber} />,
      width: '110px',
    },
    {
      field: 'date',
      title: t('dashboard.table.date'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={formatDate(data?.haiDate ?? '')} />,
      width: '96px',
    },
    {
      field: 'department',
      title: t('dashboard.table.department'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.organizationShortName}
          tooltipOverride={data?.organizationLongLabel ?? data?.organizationShortName}
          singleLine
        />
      ),
      width: getBySize({
        xl: '111px',
        lg: '100px',
        sm: '89px',
      }),
    },
    {
      field: 'haiType',
      title: t('dashboard.table.haiType'),
      render: (data) => {
        let standardTypeCodeLabel: string | undefined;
        let standardTypeTranslation: string | undefined;
        if (data.standardTypeId != null) {
          standardTypeCodeLabel = getCodeLabelForStandardTypeId(data.standardTypeId);
          standardTypeTranslation = getTranslationForStandardTypeId(data.standardTypeId);
        }
        const standardTypeData = {...data, standardTypeCodeLabel, standardTypeTranslation};
        return <HaidiHaiTypeFormatter {...AG_GRID_COMPATIBILITY} value={standardTypeData?.type} data={standardTypeData} noWrap />;
      },
      width: getBySize({
        xl: '',
        lg: '145px',
        sm: '90px',
      }),
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: () => 0,
    // ...(v.field === defaultSort.field
    //  ? {defaultSort: defaultSort.direction}
    //  : {}),
  }));
}
type Props = {
  linksLabelKey: string;
  linkStateFilter: string;
  linksData: {
    total: number | undefined;
    lastWeek: number | undefined;
    olderThanLastWeek: number | undefined;
    olderThanLastMonth: number | undefined;
  };
  dataByType: DashboardDataType['countOfReportedHaisByType'] | undefined;
  dataByOrigin: DashboardDataType['countOfReportedHaisByOrganization'] | undefined;
  noHaisLabelKey: string;
  recentHaisLabelKey: string;
  recentHais: RowType[] | undefined;
  noRecentHaisLabelKey: string;
  barChartColor: ChartColor;
  formatters: Formatters;
  lastWeekMonthIso: LastWeekMonthIso;
  config: ConfigObjectType;
};
export const DashboardColumn: React.FC<Props> = (props) => {
  const {
    linksLabelKey,
    linkStateFilter,
    linksData,
    dataByType,
    dataByOrigin,
    noHaisLabelKey,
    recentHaisLabelKey,
    recentHais,
    noRecentHaisLabelKey,
    barChartColor,
    formatters,
    lastWeekMonthIso,
    config,
  } = props;
  const {t, tEnum} = formatters;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const boundDataByType: LightBarChartTrace[] = getBarChartItems(
    sortBy(dataByType ?? [], (v) => tEnum('HaiTypeEnum', v.type)),
    [barChartColor],
    (value) => ({
      x: value.count,
      y: tEnum('HaiTypeEnum', value.type),
    })
  );
  const boundDataByOrigin: LightBarChartTrace[] = getBarChartItems(
    sortBy(dataByOrigin ?? [], (v) => v.name),
    [barChartColor],
    (value) => ({
      x: value.count,
      y: value.name,
    })
  );
  const barChartLayout: LightChartLayout = {
    xaxis: {
      automargin: true,
    },
    yaxis: {
      automargin: true,
      fixedrange: true,
    },
    showlegend: false,
    margin: {
      t: 8,
    },
  };
  const columns = useColumns(formatters, config);
  const getHaisLinkQuery = (hais: HaiListLinkFilters = {}) => ({
    // NOTE: this replaces all filters
    hais: {
      states: [linkStateFilter],
      detectionDate: null,
      ...hais,
    } as HaiListLinkFilters,
  });
  const totalLinkQuery = getHaisLinkQuery();
  const lastWeekLinkQuery = getHaisLinkQuery({
    detectionDate: {
      startDate: lastWeekMonthIso.lastWeekStart,
      endDate: lastWeekMonthIso.lastWeekEnd,
    },
  });
  const olderThanLastWeekQuery = getHaisLinkQuery({
    detectionDate: {
      startDate: lastWeekMonthIso.olderThan7DaysStart,
      endDate: lastWeekMonthIso.olderThan7DaysEnd,
    },
  });
  const olderThanLastMonthQuery = getHaisLinkQuery({
    detectionDate: {
      startDate: lastWeekMonthIso.olderThan30DaysStart,
      endDate: lastWeekMonthIso.olderThan30DaysEnd,
    },
  });
  const haisLinkQuery = getHaisLinkQuery();
  const TITLE_HEIGHT = 50;
  const chartByTypeHeight = getBarChartHeight(dataByType?.length ?? 10) + TITLE_HEIGHT;
  const chartByOriginHeight = getBarChartHeight(dataByOrigin?.length ?? 10) + TITLE_HEIGHT;
  const tableDisabled = recentHais?.length === 0;
  let tableLink = (
    <DlMuiButton
      type='chip-filled'
      title='dashboard.showAll'
      onClick={() => {
        // noop
      }}
      disabled={tableDisabled}
    />
  );
  if (!tableDisabled)
    tableLink = (
      <Link css={muiStyles.showAllLink} tabIndex={0} to={`/hais?${asQueryString(haisLinkQuery)}`}>
        {tableLink}
      </Link>
    );
  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);
  return (
    <div css={muiStyles.columnWrapper}>
      <div css={muiStyles.linksWrapper}>
        <IconHeaderWithSearch
          iconName={'barChartIconMultiColor'}
          label={linksLabelKey}
          useFixedViewBox='0 0 15 14'
          iconCss={muiStyles.headerIcon}
        />
        <div css={muiStyles.linksRow}>
          <Link css={muiStyles.totalLink} tabIndex={0} to={`/hais?${asQueryString(totalLinkQuery)}`}>
            <DlMuiDynamicIcon
              inputCss={muiStyles.totalLinkIconButton}
              svgIconInputCss={muiStyles.totalLinkIcon}
              iconName='bellIconHalfColor'
            />
            <span css={muiStyles.totalLinkSpan}>{linksData.total}</span>
          </Link>
          <div css={muiStyles.linksByTime}>
            <Link css={muiStyles.linkByTime} tabIndex={0} to={`/hais?${asQueryString(lastWeekLinkQuery)}`}>
              <span css={muiStyles.linkByTimeNumber}>{linksData.lastWeek}</span>
              <span> {t('dashboard.lastWeek')}</span>
            </Link>
            <Link css={muiStyles.linkByTime} tabIndex={0} to={`/hais?${asQueryString(olderThanLastWeekQuery)}`}>
              <span css={muiStyles.linkByTimeNumber}>{linksData.olderThanLastWeek}</span>
              <span> {t('dashboard.olderThan7Days')}</span>
            </Link>
            <Link css={muiStyles.linkByTime} tabIndex={0} to={`/hais?${asQueryString(olderThanLastMonthQuery)}`}>
              <span css={muiStyles.linkByTimeNumber}>{linksData.olderThanLastMonth}</span>
              <span> {t('dashboard.olderThan30Days')}</span>
            </Link>
          </div>
        </div>
      </div>
      {dataByType?.length === 0 && dataByOrigin?.length === 0 ? (
        <NoHais labelKey={noHaisLabelKey} />
      ) : (
        <>
          <div css={muiStyles.chartWrapper} style={{height: chartByTypeHeight}}>
            <LightChart
              data={boundDataByType}
              layout={{
                ...barChartLayout,
                title: {
                  text: t('dashboard.byHaiType'),
                  font: {
                    color: '#9BA8BD',
                  },
                  x: 0,
                },
              }}
              imageDownload={{}}
              isLoading={dataByType == null}
              noDataComponent={noDataComponent}
            />
          </div>
          <div css={muiStyles.chartWrapper} style={{height: chartByOriginHeight}}>
            <LightChart
              data={boundDataByOrigin}
              layout={{
                ...barChartLayout,
                title: {
                  text: t('dashboard.byOrigin'),
                  font: {
                    color: '#9BA8BD',
                  },
                  x: 0,
                },
              }}
              imageDownload={{}}
              isLoading={dataByOrigin == null}
              noDataComponent={noDataComponent}
            />
          </div>
        </>
      )}
      <div css={muiStyles.tableWrapper}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={recentHaisLabelKey}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
          style={{paddingTop: 16, paddingBottom: 10}}
        >
          {tableLink}
        </IconHeaderWithSearch>
        {tableDisabled ? (
          <NoHais labelKey={noRecentHaisLabelKey} />
        ) : (
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(10),
              paging: false,
            }}
            variant='big'
            columns={columns}
            data={recentHais ?? []}
            isLoading={recentHais == null}
            totalCount={recentHais?.length ?? 0}
          />
        )}
      </div>
    </div>
  );
};
const getStyles: GetStylesFunction = (options) => ({
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  columnWrapper: css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  linksWrapper: css`
    width: 100%;
    flex: 0 0 auto;
  `,
  linksRow: css`
    width: 100%;
    border-radius: 16px;
    background: var(--dlThemeGray-100);
    padding: 8px 24px 8px 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
  `,
  totalLink: css`
    width: 195px;
    height: 52px;
    border-radius: 8px;
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    background: var(--dlChip2ButtonFilledBackground);
    color: var(--dlChip2ButtonFilled);
    text-decoration: none !important;
    &:not(:disabled):hover {
      background: var(--dlChip2ButtonFilledHoverBackground) !important;
      color: var(--dlChip2ButtonFilledHover);
    }
    &:not(:disabled):active {
      background: var(--dlChip2ButtonFilledActiveBackground) !important;
      color: var(--dlChip2ButtonFilledActive);
    }
  `,
  totalLinkIconButton: css`
    padding: 0;
    cursor: pointer;
  `,
  totalLinkIcon: css`
    width: 16px;
    height: 16px;
    color: var(--dlTheme-300);
  `,
  totalLinkSpan: css`
    color: white;
    font-size: 24px;
    font-family: var(--dlFontFamilySemiBold);
    line-height: 16px;
  `,
  linksByTime: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  linkByTime: css`
    border-radius: 8px;
    background: white;
    padding: 2px 8px;
    color: var(--dlThemeGray-500);
    font-size: 12px;
    font-family: var(--dlFontFamilyNormal);
  `,
  linkByTimeNumber: css`
    color: var(--dlTheme-500);
    font-size: 12px;
    font-family: var(--dlFontFamilySemiBold);
  `,
  chartWrapper: css`
    width: 100%;
    height: 400px;
    flex: 0 0 auto;
    border: 1px solid var(--dlThemeGray-300);
    border-radius: 8px;
    padding: 16px 24px 12px 24px;
  `,
  tableWrapper: css`
    width: 100%;
    flex: 0 0 auto;
  `,
});
